import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ChatWidget from '../chat/ChatWidget';
import ConnectionIndicator from '../ConnectionIndicator';
import MasqueradeControl from '../MasqueradeControl';
import CronManagement from '../cron/CronManagement';
import GlobalSearchModal from './GlobalSearchModal';
import { getUsers } from '../../models/userModel';
import api from '../../services/api'; 

const SimpleNavbar = ({ 
  sidebarOpen, 
  setSidebarOpen, 
  sidebarCollapsed, 
  setSidebarCollapsed 
}) => {
  const location = useLocation();
  const { user, can, hasRole, canMasquerade, isMasquerading, originalUser, startMasquerading, stopMasquerading, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false); 
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [masqueradeOpen, setMasqueradeOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [switchingUser, setSwitchingUser] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [cronModalOpen, setCronModalOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);

  // Helper functions to check user roles - respects masquerading
  const isIT = () => hasRole('IT') || user?.department === 'IT' || user?.department === 27 || user?.department === 21 || user?.department === 24;
  const isProcurement = () => user?.department === 26;
  const canAccessSettings = () => can('Access Settings') || isIT();
  const canAccessGlobalSearch = () => {
    // Only the original user with ID 1 can access global search (not masqueraded users)
    const actualUserId = originalUser?.user_id || user?.user_id;
    return actualUserId === 1 || actualUserId === '1';
  };
  
  // Helper to get user name from array structure
  const getUserName = useMemo(() => {
    // Try to get the full name from the user object
    const fullName = user?.user_full_name || user?.name || user?.user_name || '';
    
    // Return the full name if available
    if (fullName && typeof fullName === 'string' && fullName.trim()) {
      return fullName.trim();
    }
    
    return 'User';
  }, [user]);
  
  const getUserInitial = useMemo(() => {
    const initial = String(getUserName)?.charAt(0)?.toUpperCase() || 'U';
    return initial;
  }, [getUserName]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications');
      if (response.data.status === 1) {
        const notifs = response.data.data.notifications || [];
        setNotifications(notifs);
        setNotificationCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close dropdowns if clicking inside dropdown containers, chat widget, or help modal
      if (!event.target.closest('.dropdown-container') && 
          !event.target.closest('[class*="fixed bottom-4 right-4"]') &&
          !event.target.closest('[style*="z-index: 9999"]') &&
          !event.target.closest('.help-modal')) {
        setSettingsOpen(false);
        setLogsOpen(false); 
        // Don't close help modal from outside click handler - let modal handle its own closing
        // setHelpModalOpen(false);
        setNotificationsOpen(false);
        setProfileOpen(false);
        // Clear search when closing masquerade dropdown
        if (masqueradeOpen) {
          setSearchTerm('');
          setUsers([]);
        }
        setMasqueradeOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Fetch users when masquerade dropdown opens
  useEffect(() => {
    if (masqueradeOpen) {
      fetchUsers();
    }
  }, [masqueradeOpen]);

  // Debounced search effect - separate from initial load
  useEffect(() => {
    if (!masqueradeOpen) return;
    
    // Only debounce if there's actually a search term
    if (searchTerm.trim()) {
      const timeoutId = setTimeout(() => {
        fetchUsers();
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    } else {
      // If search term is cleared, fetch all users immediately
      fetchUsers();
    }
  }, [searchTerm]);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams();
      const trimmedSearch = searchTerm.trim();
      
      // Always use getUsers, but with filter parameter when searching
      if (trimmedSearch) {
        params.append('filter', trimmedSearch);
        params.append('pageSize', 50); // Limit results for performance
      } else {
        params.append('pageSize', 100); // Get more users when not filtering
      }
      
      const response = await getUsers(params);
      
      if (response && response.status === 1) {
        // Handle different response structures
        let userData = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            userData = response.data;
          } else if (response.data.rows && Array.isArray(response.data.rows)) {
            userData = response.data.rows;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            userData = response.data.data;
          }
        }
        setUsers(userData);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [searchTerm]);

  const handleStartMasquerading = async (targetUserId) => {
    setSwitchingUser(true);
    try {
      await startMasquerading(targetUserId);
      setMasqueradeOpen(false);
      setSearchTerm('');
      setUsers([]);
    } catch (error) {
      console.error('Error starting masquerading:', error);
    } finally {
      setSwitchingUser(false);
    }
  };

  const handleStopMasquerading = async () => {
    setSwitchingUser(true);
    try {
      await stopMasquerading();
      setMasqueradeOpen(false);
      setSearchTerm('');
      setUsers([]);
    } catch (error) {
      console.error('Error stopping masquerading:', error);
    } finally {
      setSwitchingUser(false);
    }
  };

  return (
    <>
    <nav className="bg-gradient-to-r from-white via-gray-50 to-white shadow-lg border-b-2 border-blue-100 h-16 flex-shrink-0 flex items-center justify-between px-4 lg:px-6 backdrop-blur-sm z-10">
      {/* Left section */}
      <div className="flex items-center gap-3">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg lg:hidden transition-all duration-200 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16" />
          </svg>
        </button>

        {/* Collapse Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 text-sm font-medium border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
          title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
              d={sidebarCollapsed ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M11 19l-7-7 7-7M19 19l-7-7 7-7"} />
          </svg>
        </button>
        
        {/* Navigation Links */}
        <div className="hidden md:flex items-center gap-2">
          {/* Settings Dropdown */}
          {canAccessSettings() && (
            <div className="relative dropdown-container">
              <button 
                onClick={() => setSettingsOpen(!settingsOpen)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 text-sm font-medium border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>System</span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {settingsOpen && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-3 z-20 animate-in slide-in-from-top-2 duration-200">
                  <div className="px-4 py-2 border-b border-gray-100 mb-2">
                    <h3 className="font-semibold text-gray-900 text-sm">System</h3>
                  </div>
                  <a href="/dashboard/settings" className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                    Settings
                  </a>
                  <Link to="/dashboard/updates" className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Updates
                  </Link>
                  <div className="px-4 py-2 border-t border-gray-100 mt-2">
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Activity Logs</h4>
                  </div> 
                  <Link to="/dashboard/logs/activity" className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Activity Logs
                  </Link>
                  <Link to="/dashboard/logs/system" className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                    System Logs
                  </Link>
                  {isIT() && (
                    <>
                      <div className="px-4 py-2 border-t border-gray-100 mt-2">
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">System Management</h4>
                      </div>
                      <button
                        onClick={() => {
                          setCronModalOpen(true);
                          setSettingsOpen(false);
                        }}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors w-full text-left"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Cron Jobs
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

     
          {/* SharePoint Link - Enhanced */}
          <a 
            href="https://login.microsoftonline.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 text-sm font-medium border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            <span className="hidden xl:inline">SharePoint</span>
          </a>
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2"> 
          
          {/* Support Button Group - Helpdesk Only */}
          <div className="flex items-center bg-white border border-gray-200 rounded-lg shadow-sm overflow-visible">
            {/* Helpdesk Button */}
            <Link 
              to="/dashboard/helpdesk"   
              className="flex items-center gap-2 px-4 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 text-sm font-medium hover:border-blue-300 group"
              title="Open Helpdesk"
            >
              <svg className="w-4 h-4 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden lg:inline font-medium">Helpdesk</span>
            </Link>
          </div> 

        {/* Notifications */}
        <div className="relative dropdown-container">
          <button 
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
            title="Notifications"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </button>
          
          {notificationsOpen && (
            <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 py-3 z-20 animate-in slide-in-from-top-2 duration-200">
              <div className="px-4 py-2 border-b border-gray-100 mb-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  {notificationCount > 0 && (
                    <span className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded-full">
                      {notificationCount}
                    </span>
                  )}
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-4 text-center">
                    <div className="text-gray-500 mb-2">
                      <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600">All caught up!</p>
                    <p className="text-xs text-gray-500 mt-1">No new notifications at this time</p>
                  </div>
                ) : (
                  <div className="py-2">
                    {notifications.map((notification) => (
                      <Link
                        key={notification.id}
                        to={notification.action_url}
                        onClick={() => setNotificationsOpen(false)}
                        className="block px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            notification.priority === 'high' ? 'bg-red-500' : 'bg-blue-500'
                          }`}></div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">
                              {notification.title}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {notification.message}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {new Date(notification.created_at).toLocaleString()}
                            </div>
                          </div>
                          {notification.type === 'update' && (
                            <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                          )}
                          {notification.type === 'rfq' && (
                            <svg className="w-4 h-4 text-orange-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                          {notification.type === 'requisition' && (
                            <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                          {notification.type === 'helpdesk' && (
                            <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
                            </svg>
                          )}
                          {notification.type === 'helpdesk_ratings' && (
                            <svg className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          )}
                          {notification.type === 'asset_declaration' && (
                            <svg className="w-4 h-4 text-purple-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                          )}
                          {notification.type === 'asset_hod_approval' && (
                            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Global Search - Admin Only (user_id = 1) */}
        {canAccessGlobalSearch() && (
          <button 
            onClick={() => setGlobalSearchOpen(true)}
            className="relative p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
            title="Global Search - Search across all departments"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        )}

        {/* Admin Tools - Styled like notification icon */}
        {canMasquerade() && (
          <div className="relative dropdown-container">
            <button 
              onClick={() => setMasqueradeOpen(!masqueradeOpen)}
              className="relative p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
              title="Admin Tools - User Masquerading"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>
            {masqueradeOpen && (
              <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 py-3 z-20 animate-in slide-in-from-top-2 duration-200 max-h-96 flex flex-col">
                <div className="px-4 py-2 border-b border-gray-100 mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">Switch User</h3>
                  {isMasquerading && (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                      <div className="font-medium text-orange-800">
                        Currently: {getUserName}
                      </div>
                      <div className="text-orange-600 text-xs">
                        Original: {originalUser?.user_full_name || originalUser?.name}
                      </div>
                      <button
                        onClick={handleStopMasquerading}
                        disabled={switchingUser}
                        className="mt-1 px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
                      >
                        {switchingUser ? 'Returning...' : 'Return to Original'}
                      </button>
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search users by name, email, or department..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full mt-2 px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {loadingUsers && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {loadingUsers ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      Loading users...
                    </div>
                  ) : users.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      {searchTerm ? 'No users found' : 'No users available'}
                    </div>
                  ) : (
                    <div className="py-2">
                      {users.map((userData) => {
                        const userId = userData.user_id || userData.id;
                        const userName = userData.user_full_name || userData.name || 'Unknown User';
                        const userEmail = userData.user_email || userData.email || '';
                        const userDept = userData.department_name || '';
                        const isCurrentUser = userId === user?.user_id;
                        
                        return (
                          <button
                            key={userId}
                            onClick={() => handleStartMasquerading(userId)}
                            disabled={switchingUser || isCurrentUser}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              isCurrentUser ? 'bg-gray-100' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {userName.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {userName}
                                  {isCurrentUser && <span className="text-blue-600 ml-2">(Current)</span>}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  {userEmail}
                                </div>
                                {userDept && (
                                  <div className="text-xs text-gray-400 truncate">
                                    {userDept}
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile Dropdown - Enhanced with brand colors */}
        <div className="relative dropdown-container">
          <button 
            onClick={() => setProfileOpen(!profileOpen)}
            className={`flex items-center gap-3 px-4 py-2 text-white rounded-xl transition-all duration-200 text-sm shadow-md hover:shadow-lg ${
              isMasquerading 
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700' 
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
            }`}
            style={{
              background: isMasquerading 
                ? 'linear-gradient(135deg, #fd7e14, #e8590c)' 
                : 'var(--gradient-primary)'
            }}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
              isMasquerading 
                ? 'bg-orange-100 text-orange-800 border-orange-200' 
                : 'bg-white text-blue-700 border-blue-100'
            }`}>
              {getUserInitial}
            </div>
            <div className="hidden lg:flex flex-col items-start min-w-0 flex-1">
              <span className="text-sm font-semibold truncate max-w-full">
                {isMasquerading ? '🎭 ' : ''}{getUserName}
              </span>
              <span className="text-xs opacity-90 truncate max-w-full">
                {isMasquerading ? 'Troubleshooting Mode' : (user?.role_name || 'User')}
              </span>
            </div>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {profileOpen && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-3 z-20 animate-in slide-in-from-top-2 duration-200">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                    isMasquerading 
                      ? 'bg-orange-100 text-orange-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {getUserInitial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">
                      {isMasquerading && '🎭 '}{getUserName}
                    </div>
                    <div className="text-sm text-gray-600 truncate">
                      {isMasquerading ? 'Masquerading Mode' : (user?.role_name || 'User')}
                    </div>
                  </div>
                </div>
                {isMasquerading && originalUser && (
                  <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                    <div className="text-orange-800 font-medium mb-1">
                      Originally: {originalUser.user_full_name || originalUser.name}
                    </div>
                    <button
                      onClick={handleStopMasquerading}
                      disabled={switchingUser}
                      className="px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
                    >
                      {switchingUser ? 'Returning...' : 'Return to Original User'}
                    </button>
                  </div>
                )}
              </div>
              <div className="py-2">
                <Link to="/dashboard/profile" className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  My Profile
                </Link>
                 <Link to="/dashboard/reload" className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reload Permissions
                </Link>
                {/* <Link to="/dashboard/settings" className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Account Settings
                </Link> */}
                <button 
                  onClick={async () => {
                    try {
                      await logout();
                      // Logout function in authService.js already handles redirect to /login
                    } catch (error) {
                      console.error('Logout error:', error);
                      // Fallback redirect if logout fails
                      window.location.href = '/login';
                    }
                  }}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
    
    {/* Cron Management Modal */}
    <CronManagement isOpen={cronModalOpen} onClose={() => setCronModalOpen(false)} />
    
    {/* Global Search Modal */}
    <GlobalSearchModal isOpen={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} />
  
  </>
  );
};

export default SimpleNavbar;
